import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

const COOKIE_CONSENT_KEY = 'c2_analytics_consent';
const COOKIE_CONSENT_EXPIRES_DAYS = 365;

interface CookieConsent {
  analytics: boolean;
  timestamp: number;
}

declare global {
  interface Window {
    revokeAnalyticsConsent?: () => void;
    __GA4_INIT__?: boolean;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// ─── GA4 tracking ────────────────────────────────────────────────────────────
async function initGA4Tracking(): Promise<void> {
  if (typeof window === 'undefined' || window.__GA4_INIT__) return;

  try {
    const res = await fetch('/api/config/analytics');
    if (!res.ok) return;
    const { measurementId } = await res.json() as { measurementId: string | null };
    if (!measurementId) return;

    window.__GA4_INIT__ = true;

    // Bootstrap dataLayer + gtag helper
    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      // Anonymise IPs for GDPR compliance
      anonymize_ip: true,
      // Don't send a pageview on load — we fire it manually below
      send_page_view: false,
    });

    // Load the gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);

    // Fire initial pageview
    window.gtag('event', 'page_view', {
      page_path: location.pathname,
      page_title: document.title,
    });

    // Track SPA route changes
    let lastPath = location.pathname;
    const trackPage = () => {
      if (location.pathname !== lastPath) {
        lastPath = location.pathname;
        window.gtag!('event', 'page_view', {
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    };
    window.addEventListener('popstate', trackPage);
    const push = history.pushState;
    const replace = history.replaceState;
    history.pushState = (...args) => { push.apply(history, args); setTimeout(trackPage, 0); };
    history.replaceState = (...args) => { replace.apply(history, args); setTimeout(trackPage, 0); };
  } catch {
    // Silently fail — analytics should never break the site
  }
}


/**
 * Cookie banner component for C2 analytics consent
 *
 * Displays a consent banner for C2 analytics tracking. Manages user consent
 * preferences in localStorage and controls whether analytics scripts are loaded.
 */
export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(function checkConsent() {
    if (typeof window === 'undefined') return;

    const consentData = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!consentData) {
      setShowBanner(true);
      setIsLoaded(true);
      return;
    }

    try {
      const consent: CookieConsent = JSON.parse(consentData);
      const daysSinceConsent = (Date.now() - consent.timestamp) / (1000 * 60 * 60 * 24);

      if (daysSinceConsent > COOKIE_CONSENT_EXPIRES_DAYS) {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        setShowBanner(true);
      } else if (consent.analytics) {
        void initGA4Tracking();
      }
    } catch {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      setShowBanner(true);
    }

    setIsLoaded(true);
  }, []);

  function saveConsent(analytics: boolean) {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ analytics, timestamp: Date.now() }));
    if (analytics) {
      void initGA4Tracking();
    }
    setShowBanner(false);
  }

  function revokeConsent() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setShowBanner(true);
  }

  useEffect(function exposeRevokeFunction() {
    if (typeof window === 'undefined') return;
    window.revokeAnalyticsConsent = revokeConsent;
    return () => { delete window.revokeAnalyticsConsent; };
  }, []);

  if (!isLoaded || !showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t shadow-lg"
      style={{
        background: 'hsl(var(--card))',
        borderColor: 'hsl(var(--border))',
      }}
      role="alertdialog"
      aria-live="polite"
      aria-label="Cookie consent banner"
      aria-describedby="cookie-banner-description"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground mb-1">Cookie Consent</h3>
            <p id="cookie-banner-description" className="text-sm text-foreground/90">
              We use cookies to enable essential services and to understand how visitors interact with our site. This includes Google Analytics for traffic analysis. By clicking Accept, you agree to our use of these tools for analytics and support.{' '}
              <a
                href="/privacy-policy"
                aria-label="Learn more about how we use cookies"
                className="font-medium text-foreground underline underline-offset-2 hover:opacity-80"
              >
                Read our privacy policy
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => saveConsent(false)} className="whitespace-nowrap">Decline</Button>
            <Button size="sm" onClick={() => saveConsent(true)} className="whitespace-nowrap" autoFocus>Accept</Button>
          </div>

        </div>
      </div>
    </div>
  );
}
